import type { MouseEventHandler, ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { parseExtraGuestGeneratedPolicies } from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesContent'
import ModalContext from 'contexts/ModalContext'
import { useInlineListFormatter } from 'hooks/useListFormatters'
import PackageInclusionsPolicyModal from './PackageInclusionsPolicyModal'

const ValidityText = styled(BodyText)`
  margin-top: ${rem(8)};
`

type Props = {
  policyDescription?: string
  selectedOccupancy?: App.Occupants
}

function getGeneratedPackageInclusions(policyDescription?: string): Array<string> {
  return parseExtraGuestGeneratedPolicies(policyDescription)?.packageInclusions.filter(Boolean) ?? []
}

export function hasStructuredPackageInclusionsValidityText(policyDescription?: string): boolean {
  return getGeneratedPackageInclusions(policyDescription).length > 0
}

function formatIncludedGuestCombination(intl: ReturnType<typeof useIntl>, includedGuests: App.PackageRoomIncludedGuests): string {
  const parts = [
    includedGuests.adults > 0 ? intl.formatMessage(
      {
        id: 'MauzMmZvkQuvuh11L00p',
        defaultMessage: '{count, plural, one {# adult} other {# adults}}',
        description: 'Adult guest count included in the package inclusions validity note',
      },
      { count: includedGuests.adults },
    ) : '',
    includedGuests.teenagers > 0 ? intl.formatMessage(
      {
        id: 'alkqCuYpoGP0UBwjqurw',
        defaultMessage: '{count, plural, one {# teenager} other {# teenagers}}',
        description: 'Teenage guest count included in the package inclusions validity note',
      },
      { count: includedGuests.teenagers },
    ) : '',
    includedGuests.children > 0 ? intl.formatMessage(
      {
        id: 'LD0dqRyj_xansDw80LtZ',
        defaultMessage: '{count, plural, one {# child} other {# children}}',
        description: 'Child guest count included in the package inclusions validity note',
      },
      { count: includedGuests.children },
    ) : '',
    includedGuests.infants > 0 ? intl.formatMessage(
      {
        id: 'Sm31_-fCutJQJfQh3gVo',
        defaultMessage: '{count, plural, one {# infant} other {# infants}}',
        description: 'Infant guest count included in the package inclusions validity note',
      },
      { count: includedGuests.infants },
    ) : '',
  ].filter(Boolean)

  return parts.join(', ')
}

function getMatchingGeneratedPackageInclusion(
  intl: ReturnType<typeof useIntl>,
  generatedPackageInclusions: Array<string>,
  selectedOccupancy?: App.Occupants,
): string | undefined {
  if (!selectedOccupancy) return undefined

  const selectedOccupancyText = formatIncludedGuestCombination(intl, {
    adults: selectedOccupancy.adults,
    teenagers: selectedOccupancy.teenagers ?? 0,
    children: selectedOccupancy.children ?? 0,
    infants: selectedOccupancy.infants ?? 0,
  })

  return generatedPackageInclusions.find(packageInclusion => packageInclusion === selectedOccupancyText)
}

function getIncludedGuestCombinations(
  intl: ReturnType<typeof useIntl>,
  policyDescription?: string,
  selectedOccupancy?: App.Occupants,
): Array<string> {
  const generatedPackageInclusions = getGeneratedPackageInclusions(policyDescription)

  const matchingGeneratedPackageInclusion = getMatchingGeneratedPackageInclusion(intl, generatedPackageInclusions, selectedOccupancy)

  if (matchingGeneratedPackageInclusion) {
    return [matchingGeneratedPackageInclusion]
  }

  return generatedPackageInclusions
}

function PackageInclusionsValidityText({ policyDescription, selectedOccupancy }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const formatDisjunctionList = useInlineListFormatter('disjunction')

  const includedGuestCombinations = useMemo(() => getIncludedGuestCombinations(intl, policyDescription, selectedOccupancy), [intl, policyDescription, selectedOccupancy])
  const validOccupancies = useMemo(() => formatDisjunctionList(includedGuestCombinations), [formatDisjunctionList, includedGuestCombinations])
  const onViewPolicyClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()

    if (policyDescription) {
      showModal(<PackageInclusionsPolicyModal policyDescription={policyDescription} />)
    }
  }, [policyDescription, showModal])

  return (
    <ValidityText variant="small" colour="neutral-three">
      {policyDescription && intl.formatMessage(
        {
          id: 'X05WkOlMVILmoKWfgCYz',
          defaultMessage: 'Inclusions valid for {validOccupancies}. Extra guest inclusions may vary. <link>View policy</link>',
          description: 'Note shown below an accommodation package explaining which guest combinations receive the package inclusions with a link to policy details',
        },
        {
          validOccupancies,
          link: (chunks: ReactNode) => <TextLink size="small" colour="neutral-three" weight="regular" key="view-policy" onClick={onViewPolicyClick}>{chunks}</TextLink>,
        },
      )}
      {!policyDescription && intl.formatMessage(
        {
          id: 'cgVMOZx4SClEKWt6xesD',
          defaultMessage: 'Inclusions valid for {validOccupancies}. Extra guest inclusions may vary.',
          description: 'Note shown below an accommodation package explaining which guest combinations receive the package inclusions',
        },
        { validOccupancies },
      )}
    </ValidityText>
  )
}

export default PackageInclusionsValidityText
